import { Invite, MotCle } from './types';

export function normaliser(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

export async function sha256Hex(s: string): Promise<string> {
  const data = new TextEncoder().encode(s);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

const CLE_OBF = 'bw-2026-petale';

// Obfuscation légère (XOR + base64) : sert uniquement à la tolérance Levenshtein
// et à la révélation de lettres ; la validation exacte passe par SHA-256.
export function deobf(b64: string): string {
  const bytes = atob(b64);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += String.fromCharCode(bytes.charCodeAt(i) ^ CLE_OBF.charCodeAt(i % CLE_OBF.length));
  }
  return out;
}

export async function verifierMot(saisie: string, mot: MotCle): Promise<boolean> {
  const n = normaliser(saisie);
  if (!n) return false;
  const h = await sha256Hex(n);
  if (h === mot.reponseHash || mot.variantesHash.includes(h)) return true;
  const reponse = normaliser(deobf(mot.reponseObf));
  const tolerance = reponse.length >= 10 ? 2 : 1;
  return levenshtein(n, reponse) <= tolerance;
}

export function graine(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function melanger<T>(arr: T[], rand: () => number): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Permutation reproductible par graine, sans jamais 3 indices du même groupe d'affilée
export function genererPermutation(invites: Invite[], prenom: string, demarreLe: number): string[] {
  const rand = mulberry32(graine(`${normaliser(prenom)}:${demarreLe}`));
  const ordre = melanger(invites, rand);
  for (let essai = 0; essai < 50; essai++) {
    let corrige = false;
    for (let i = 2; i < ordre.length; i++) {
      if (ordre[i].groupe === ordre[i - 1].groupe && ordre[i].groupe === ordre[i - 2].groupe) {
        const j = ordre.findIndex((inv, k) => k > i && inv.groupe !== ordre[i].groupe);
        if (j > i) {
          [ordre[i], ordre[j]] = [ordre[j], ordre[i]];
          corrige = true;
        }
      }
    }
    if (!corrige) break;
  }
  return ordre.map((o) => o.id);
}

export function formatTemps(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export async function calculerJeton(prenom: string, demarreLe: number, tempsSec: number, sel: string): Promise<string> {
  const h = await sha256Hex(`${normaliser(prenom)}:${demarreLe}:${tempsSec}:${sel}`);
  return h.slice(0, 4).toUpperCase();
}

export function assetUrl(chemin: string): string {
  return `${process.env.PUBLIC_URL || ''}/${chemin}`;
}
